import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Trophy, CheckCircle, XCircle, Star, RotateCcw, Home, BookOpen, Share2, Download, Copy, Loader2, Swords } from 'lucide-react';
import { quizService } from '../services/quizService';
import { useToast } from '../hooks/use-toast';
import { copyChallengeLink, downloadResultCard, shareResult } from '../lib/resultShare';

const QuizResult = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [result, setResult] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [challengeUrl, setChallengeUrl] = useState('');
  const [shareBusy, setShareBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    // Get result from localStorage
    const savedResult = localStorage.getItem('quizResult');
    if (savedResult) {
      const parsedResult = JSON.parse(savedResult);
      if (parsedResult.quizId === quizId) {
        setResult(parsedResult);
      } else {
        navigate('/');
      }
    } else {
      navigate('/');
    }
  }, [quizId, navigate]);

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-blue-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBadge = (score) => {
    if (score >= 90) return { text: 'Excellent!', className: 'bg-green-100 text-green-800 border-green-200' };
    if (score >= 70) return { text: 'Good Job!', className: 'bg-blue-100 text-blue-800 border-blue-200' };
    if (score >= 50) return { text: 'Not Bad', className: 'bg-yellow-100 text-yellow-800 border-yellow-200' };
    return { text: 'Keep Trying', className: 'bg-red-100 text-red-800 border-red-200' };
  };

  const ensureChallengeUrl = async () => {
    if (challengeUrl) return challengeUrl;
    const created = await quizService.createChallenge(quizId, {
      score: result.score,
      correct: result.correct,
      total: result.total,
    });
    const url = new URL(created.challenge_path, window.location.origin).toString();
    setChallengeUrl(url);
    return url;
  };

  const createChallenge = async () => {
    setShareBusy(true);
    try {
      await ensureChallengeUrl();
    } catch (error) {
      toast({ title: 'Could not create challenge', description: 'Please try again.', variant: 'destructive' });
    } finally {
      setShareBusy(false);
    }
  };

  const copyChallenge = async () => {
    setShareBusy(true);
    try {
      const url = await ensureChallengeUrl();
      await copyChallengeLink(url);
      toast({ title: 'Challenge link copied', description: 'Send it to a friend to see if they can beat you.' });
    } catch (error) {
      toast({ title: 'Could not copy link', description: 'Please try again.', variant: 'destructive' });
    } finally {
      setShareBusy(false);
    }
  };

  const shareChallenge = async () => {
    setShareBusy(true);
    try {
      const url = await ensureChallengeUrl();
      const shared = await shareResult(result, url);
      if (!shared) {
        await copyChallengeLink(url);
        toast({ title: 'Challenge link copied', description: 'Sharing is not supported here, so the link is ready to paste.' });
      }
    } catch (error) {
      if (error?.name !== 'AbortError') {
        toast({ title: 'Could not share result', description: 'Please try again.', variant: 'destructive' });
      }
    } finally {
      setShareBusy(false);
    }
  };

  const downloadCard = async () => {
    setDownloading(true);
    try {
      await downloadResultCard(result);
    } catch (error) {
      toast({ title: 'Could not download score card', description: error.message, variant: 'destructive' });
    } finally {
      setDownloading(false);
    }
  };

  const retakeQuiz = () => {
    localStorage.removeItem('quizResult');
    navigate(`/quiz/${quizId}`);
  };

  if (!result) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      </Layout>
    );
  }

  const scoreBadge = getScoreBadge(result.score);

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Result Header */}
          <Card className="bg-white/80 backdrop-blur-sm shadow-2xl border-0 mb-8">
            <CardHeader className="text-center pb-6">
              <div className="mb-6">
                <div className="relative mx-auto w-24 h-24 mb-4">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center shadow-lg">
                    <Trophy className="h-12 w-12 text-white" />
                  </div>
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-600/20 to-purple-600/20 blur-xl" />
                </div>
                
                <Badge className={`text-lg px-4 py-2 ${scoreBadge.className}`}>
                  {scoreBadge.text}
                </Badge>
              </div>
              
              <CardTitle className="text-4xl font-bold text-gray-900 mb-2">
                Quiz Completed!
              </CardTitle>
              <p className="text-xl text-gray-600">{result.quizTitle}</p>
              <p className="text-lg text-gray-500">{result.bookTitle}</p>
            </CardHeader>
            
            <CardContent className="space-y-8">
              {/* Score Display */}
              <div className="text-center">
                <div className={`text-6xl font-bold mb-2 ${getScoreColor(result.score)}`}>
                  {result.score}%
                </div>
                <p className="text-gray-600 text-lg">
                  {result.correct} out of {result.total} questions correct
                </p>
                <div className="mt-4 max-w-md mx-auto">
                  <Progress value={result.score} className="h-3" />
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-6 bg-green-50 rounded-xl border border-green-200">
                  <CheckCircle className="h-10 w-10 text-green-600 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-green-800">{result.correct}</div>
                  <div className="text-green-600">Correct</div>
                </div>
                
                <div className="text-center p-6 bg-red-50 rounded-xl border border-red-200">
                  <XCircle className="h-10 w-10 text-red-600 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-red-800">{result.total - result.correct}</div>
                  <div className="text-red-600">Incorrect</div>
                </div>
                
                <div className="text-center p-6 bg-blue-50 rounded-xl border border-blue-200">
                  <Star className="h-10 w-10 text-blue-600 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-blue-800">{Math.ceil(result.score / 20)}/5</div>
                  <div className="text-blue-600">Stars</div>
                </div>
              </div>

              {result.challengeTargetScore !== null && result.challengeTargetScore !== undefined && (
                <div className={'rounded-xl border p-5 text-center ' + (result.score > result.challengeTargetScore ? 'border-green-200 bg-green-50' : 'border-purple-200 bg-purple-50')}>
                  <Swords className={'h-7 w-7 mx-auto mb-2 ' + (result.score > result.challengeTargetScore ? 'text-green-600' : 'text-purple-600')} />
                  <p className="font-semibold text-gray-900">
                    {result.score > result.challengeTargetScore
                      ? 'Challenge won! You beat ' + result.challengeTargetScore + '%.'
                      : result.score === result.challengeTargetScore
                        ? 'It is a tie at ' + result.score + '%.'
                        : 'You scored ' + result.score + '%. Try again to beat ' + result.challengeTargetScore + '%.'}
                  </p>
                </div>
              )}

              <div className="border-t border-gray-200 pt-7 text-center">
                <h3 className="text-xl font-bold text-gray-900">Challenge your friends</h3>
                <p className="text-gray-600 mt-1 mb-4">Share your score and give friends a unique score-to-beat link.</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button onClick={shareChallenge} disabled={shareBusy} className="min-h-[44px]">
                    {shareBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Share2 className="h-4 w-4 mr-2" />}
                    Share Challenge
                  </Button>
                  <Button onClick={copyChallenge} disabled={shareBusy} variant="outline" className="min-h-[44px]">
                    <Copy className="h-4 w-4 mr-2" /> Copy Link
                  </Button>
                  <Button onClick={downloadCard} disabled={downloading} variant="outline" className="min-h-[44px]">
                    {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                    Score Card
                  </Button>
                </div>
                {!challengeUrl && (
                  <button onClick={createChallenge} disabled={shareBusy} className="mt-3 text-sm text-blue-600 hover:underline disabled:opacity-50">
                    Create the link without sharing
                  </button>
                )}
                {challengeUrl && (
                  <div className="mt-5 rounded-lg bg-gray-50 border border-gray-200 p-4">
                    <p className="text-sm text-green-700 font-medium mb-3">Your challenge link is ready</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      <a className="text-sm px-3 py-2 rounded-md bg-green-600 text-white hover:bg-green-700" target="_blank" rel="noopener noreferrer" href={'https://wa.me/?text=' + encodeURIComponent('Can you beat my ' + result.score + '% score on ' + result.quizTitle + '? ' + challengeUrl)}>WhatsApp</a>
                      <a className="text-sm px-3 py-2 rounded-md bg-gray-900 text-white hover:bg-black" target="_blank" rel="noopener noreferrer" href={'https://twitter.com/intent/tweet?text=' + encodeURIComponent('I scored ' + result.score + '% on ' + result.quizTitle + '. Can you beat me?') + '&url=' + encodeURIComponent(challengeUrl)}>X</a>
                      <a className="text-sm px-3 py-2 rounded-md bg-blue-700 text-white hover:bg-blue-800" target="_blank" rel="noopener noreferrer" href={'https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(challengeUrl)}>Facebook</a>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => setShowReview(!showReview)}
                  variant="outline"
                  className="min-h-[44px] touch-manipulation hover:scale-105 transition-all"
                >
                  <BookOpen className="h-4 w-4 mr-2" />
                  {showReview ? 'Hide Review' : 'Review Answers'}
                </Button>
                
                <Button
                  onClick={retakeQuiz}
                  className="min-h-[44px] touch-manipulation bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all hover:scale-105"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Retake Quiz
                </Button>
                
                <Link to="/">
                  <Button variant="outline" className="w-full min-h-[44px] touch-manipulation hover:scale-105 transition-all">
                    <Home className="h-4 w-4 mr-2" />
                    Back to Home
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Answer Review */}
          {showReview && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">Answer Review</h2>
              
              {result.questions.map((question, index) => {
                const userAnswer = result.answers[question.id];
                const isCorrect = userAnswer === question.correct_answer;
                
                return (
                  <Card key={question.id} className="bg-white/80 backdrop-blur-sm shadow-lg border-0">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <CardTitle className="text-lg text-gray-900 flex-1">
                          Question {index + 1}: {question.question}
                        </CardTitle>
                        <div className="ml-4">
                          {isCorrect ? (
                            <CheckCircle className="h-6 w-6 text-green-600" />
                          ) : (
                            <XCircle className="h-6 w-6 text-red-600" />
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="space-y-4">
                      {question.type === 'multiple_choice' ? (
                        <div className="space-y-2">
                          {question.options.map((option, optionIndex) => {
                            let className = "p-3 rounded-lg border ";
                            
                            if (optionIndex === question.correct_answer) {
                              className += "bg-green-50 border-green-300 text-green-800";
                            } else if (optionIndex === userAnswer && !isCorrect) {
                              className += "bg-red-50 border-red-300 text-red-800";
                            } else {
                              className += "bg-gray-50 border-gray-200 text-gray-700";
                            }
                            
                            return (
                              <div key={optionIndex} className={className}>
                                <div className="flex items-center">
                                  {optionIndex === question.correct_answer && (
                                    <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                                  )}
                                  {optionIndex === userAnswer && !isCorrect && (
                                    <XCircle className="h-4 w-4 text-red-600 mr-2" />
                                  )}
                                  <span>{option}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {[
                            { value: true, label: 'True' },
                            { value: false, label: 'False' }
                          ].map((option) => {
                            let className = "p-3 rounded-lg border ";
                            
                            if (option.value === question.correct_answer) {
                              className += "bg-green-50 border-green-300 text-green-800";
                            } else if (option.value === userAnswer && !isCorrect) {
                              className += "bg-red-50 border-red-300 text-red-800";
                            } else {
                              className += "bg-gray-50 border-gray-200 text-gray-700";
                            }
                            
                            return (
                              <div key={option.value} className={className}>
                                <div className="flex items-center">
                                  {option.value === question.correct_answer && (
                                    <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                                  )}
                                  {option.value === userAnswer && !isCorrect && (
                                    <XCircle className="h-4 w-4 text-red-600 mr-2" />
                                  )}
                                  <span className="font-medium">{option.label}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {question.explanation && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="font-semibold text-blue-900 mb-2">Explanation:</h4>
                          <p className="text-blue-800">{question.explanation}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default QuizResult;